/**
 * The project's pending list: what the agents could not resolve on their own, by part of the solution.
 * Vephus's fixed rule (DocsService.PendingRule on the server) tells every agent to keep it in the
 * repository's PENDENCIAS.md and to mirror it into the team's documentation at
 * pendencias/<repository>.md, so the header's "Pendências" opens it from anywhere.
 *
 * In the desktop app the repository's file is the fresher copy: it is copied into the documentation
 * before opening. With neither, the list is created from a short template.
 */

import { api, ApiError } from './api'
import { isDesktop, repoRead } from './desktop'
import { folderFor } from './repoFolders'

export const PENDING_FILE = 'PENDENCIAS.md'

export function pendingDocPath(repository: string): string {
  return `pendencias/${repository}.md`
}

export const PENDING_TEMPLATE = `# Pendências

O que os agentes não conseguiram resolver sozinhos fica anotado aqui, separado por parte da solução
(front-end, API, app mobile, infraestrutura…). Cada item diz o que falta, por quê e o que é preciso de você.

## Geral

- [ ] Nada pendente por enquanto.
`

/** Makes sure the documentation has the list (from the repository's file, or the template); returns its path. */
export async function ensurePendingList(repository: string): Promise<string> {
  const path = pendingDocPath(repository)
  let local: string | null = null
  const folder = isDesktop() ? folderFor(repository) : null
  if (folder) {
    try {
      local = await repoRead(folder, PENDING_FILE)
    } catch {
      // No file in the repository yet.
    }
  }
  let existing: string | null = null
  try {
    existing = (await api.docs.byPath(path)).content
  } catch (error) {
    // No document at that path yet: the API says not_found (409), or 404 from an older server.
    if (!(error instanceof ApiError) || (error.status !== 404 && error.code !== 'not_found')) throw error
  }
  const wanted = local?.trim() ? local : existing ?? PENDING_TEMPLATE
  if (wanted !== existing) await api.docs.upsertByPath(path, wanted)
  return path
}
